/// <amd-module name="Repos/Artifact/Discussion/ArtifactDiscussionUIService" />
import * as Resources from "Repos/Artifact/Discussion/Resources";
import { CommentThreadStatus } from "Repos/Common/Contracts/Git";
import { IDiscussionThreadModel } from "Repos/Discussion/Common/DiscussionContracts";
import { showMessageDialog } from "VSS/Features/PlatformUI/MessageDialog";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { ArtifactDiscussionThread } from "./ArtifactDiscussionContracts";
import { threadContextToProperties } from "./ArtifactDiscussionUtil";
import { CommentType, DiscussionComment } from "./Contracts/Discussion";
import { getDiscussionClient } from "./RestClient/Discussion";

export interface IArtifactDiscussionContext {
    artifactUri: string;
    thread: IDiscussionThreadModel;
}

/**
 * This server is not meant to be used directly.
 * Instead, ArtifactDiscussion component uses this services to talk
 * back to server to persist UI changes like adding a new comment,
 * updating a comment or changing the status of the discussion.
 */
export interface IArtifactDiscussionUIService extends IVssService {
    addComment(discussionContext: IArtifactDiscussionContext, commentId: number, comment: string): void;
    removeComment(discussionContext: IArtifactDiscussionContext, commentId: number, onDeleteThread?: (threadId: number) => void): void;
    updateComment(discussionContext: IArtifactDiscussionContext, commentId: number, comment: string): void;
    changeStatus(
        discussionContext: IArtifactDiscussionContext,
        status: CommentThreadStatus,
        onChangeStatus?: (status: CommentThreadStatus) => void
    ): void;
}

class ArtifactDiscussionUIService extends VssService implements IArtifactDiscussionUIService {
    public async addComment(discussionContext: IArtifactDiscussionContext, commentId: number, comment: string): Promise<void> {
        const { artifactUri, thread } = discussionContext;
        const threadId = thread.id;

        try {
            thread.startSaving(commentId);

            if (threadId > 0) {
                // Adding new comment to the existing thread
                const commentObj = {
                    commentType: CommentType.Text,
                    content: comment,
                    parentId: thread.getParentCommentId()
                } as DiscussionComment;

                const newComment = await getDiscussionClient(this.pageContext).addComment(commentObj, threadId);
                thread.addComment(newComment);
            } else {
                // Starting a new thread
                const commentThread = {
                    ...thread.thread,
                    artifactUri,
                    comments: [{ commentType: CommentType.Text, content: comment } as DiscussionComment],
                    properties: threadContextToProperties(thread.thread.threadContext),
                    status: CommentThreadStatus.Active as number
                } as ArtifactDiscussionThread;

                const newThread = await getDiscussionClient(this.pageContext).createThread(commentThread);
                thread.update({ ...newThread, threadContext: thread.thread.threadContext });
            }

            thread.finishSaving(commentId);
        } catch (error) {
            thread.setCommentError(commentId, error);
            thread.finishSaving(commentId);
        }
    }
    public async removeComment(
        discussionContext: IArtifactDiscussionContext,
        commentId: number,
        onDeleteThread?: (threadId: number) => void
    ): Promise<void> {
        const { thread } = discussionContext;
        const threadId = thread.id;

        showMessageDialog(this.pageContext, {
            message: Resources.DeleteCommentMessage,
            okButtonProps: {
                text: Resources.DeleteButtonText
            },
            onClose: async result => {
                if (result) {
                    try {
                        await getDiscussionClient(this.pageContext).deleteComment(threadId, commentId);
                        thread.deleteComment(commentId);

                        if (!thread.hasComments()) {
                            onDeleteThread && onDeleteThread(thread.id);
                        }
                    } catch (error) {
                        thread.setError(error);
                    }
                }
            },
            title: Resources.DeleteCommentMessageTitle
        });
    }

    public async updateComment(discussionContext: IArtifactDiscussionContext, commentId: number, comment: string): Promise<void> {
        const { thread } = discussionContext;
        const threadId = thread.id;

        try {
            thread.startSaving(commentId);

            const commentObj = {
                commentType: CommentType.Text,
                content: comment,
                parentId: thread.getParentCommentId()
            } as DiscussionComment;

            const updatedComment = await getDiscussionClient(this.pageContext).updateComment(commentObj, threadId, commentId);
            thread.updateComment(updatedComment);
            thread.finishSaving(commentId);
        } catch (error) {
            thread.setCommentError(commentId, error);
            thread.finishSaving(commentId);
        }
    }

    public async changeStatus(
        discussionContext: IArtifactDiscussionContext,
        status: CommentThreadStatus,
        onChangeStatus?: (status: CommentThreadStatus) => void
    ): Promise<void> {
        const { artifactUri, thread } = discussionContext;
        const threadId = thread.id;

        try {
            const statusValue = status as number;
            const patchThread = { artifactUri, status: statusValue } as ArtifactDiscussionThread;

            await getDiscussionClient(this.pageContext).updateThread(patchThread, threadId);
            thread.updateStatus(statusValue);

            onChangeStatus && onChangeStatus(statusValue);
        } catch (error) {
            thread.setError(error);
        }
    }
}

const serviceName = "IArtifactDiscussionUIService";
Services.add(serviceName, { serviceFactory: ArtifactDiscussionUIService });

export function getArtifactDiscussionUIService(pageContext: IVssPageContext): IArtifactDiscussionUIService {
    return pageContext.getService<IArtifactDiscussionUIService>(serviceName);
}
